import React, { Suspense, useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import HeaderMenuBar from 'layouts/MainHeader/HeaderMenuBar';
import { Select, Divider, Popover, Space, Modal, Badge, notification } from 'antd';
import {
  map as _map,
  isEmpty as _isEmpty,
  find as _find,
  isString as _isString,
  filter as _filter,
  some as _some,
  compact as _compact,
  every as _every,
  includes as _includes,
  delay as _delay,
  mapValues as _mapValues,
  isEqual as _isEqual
} from 'lodash-es';
import GraasBetaLogo from 'assets/images/graas_logo_small.svg';
import GraasLogo from 'assets/images/graas_logo_small-old.svg';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DashboardConfigConstants } from 'redux/DashboardConfig/DashboardConfig.constants';
import Icon from 'components/Icon/Icon';
import {
  routesMapping,
  skippedChannels,
  getInitialFilteredChannels,
  rcaIgnoreChannels,
  globalFilterRoute,
  getAllStores,
  productDeepDiveIgnoreChannels,
  getOrderIncomeSelectedStores,
  getEnabledFlag,
  onCloseModal,
  deepDiveReportEnabledChannel,
  computeCompareDateRange,
  extractSupportedChannels,
  updateQueryParams
} from 'utilities/utilities_exports';
import queryString from 'query-string';
import { HistrocalDataStatus } from 'components/HistoricalDataLog/HistoricalDataStatus';
import { LazyLoadWithRetry } from 'components/Atoms/LazyLoadWithRetry/LazyLoadWithRetry';
import { OrdersConstants } from 'redux/Orders/Orders.constants';
import { InventoryConstants } from 'redux/Inventory/Inventory.constants';
import { setChannelFilter, setChannelFilterForCustomDashboard, setIsFilterValueChanged } from 'redux/Filter/Filter';
import { ReconciliationConstants } from 'redux/Finances/Reconciliation/Reconciliation.constants';
import countryDetails from 'assets/country/CountryDetails.json';
import './MainHeader.scss';
import GraasDateRangePicker from 'components/Atoms/GraasDateRangePicker/GraasDateRangePicker';
import { updateDateRangeState } from 'redux/DateRangePicker/dateRangeSlice';
import GraasHopperV2 from 'Modules/Hopper/GraasHopperV2/GraasHopperV2';
import GraasStoreGroupPicker from 'components/Atoms/GraasStoreGroupPicker/GraasStoreGroupPicker';
import GraasChromeExtensionProcess from 'components/Atoms/GraasChromeExtensionProcess/GraasChromeExtensionProcess';
import TooltipWrapper from 'components/Atoms/TooltipWrapper/TooltipWrapper';
import { StockValidationConstants } from 'redux/StockValidation/StockValidation.constants';

const UserProfile = LazyLoadWithRetry(() =>
  import(/* webpackChunkName: 'UserProfile' */ 'components/UserProfile/UserProfile')
);

const DemoUserProfile = LazyLoadWithRetry(() =>
  import(/* webpackChunkName: 'UserProfile' */ 'components/UserProfile/DemoUserProfile')
);

const OrdersStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'OrdersStoreFilter' */ 'Modules/Execute/subModules/Orders/components/OrdersWrapper/OrdersStoreFilter/OrdersStoreFilter'
  )
);

const ProductMasterStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'ProductMasterStoreFilter' */ 'Modules/Execute/subModules/ProductMaster/components/ProductMasterDetail/ProductMasterStoreFilter'
  )
);
const ListingBatchStoreFilter = LazyLoadWithRetry(() =>
  import(
    /*webpackChunkName: 'ListingBatchStoreFilter' */ 'Modules/Execute/subModules/ListingBatch/components/ListingBatchStoreFilter'
  )
);
const OfflineOrderStoreFilter = LazyLoadWithRetry(() =>
  import(
    /*webpackChunkName: 'OfflineOrderStoreFilter' */ 'Modules/Execute/subModules/OfflineOrders/components/OfflineOrderStoreFilter'
  )
);

const InventoryStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'InventoryStoreFilter' */ 'Modules/Execute/subModules/Inventory/components/InventoryStoreFilter/InventoryStoreFilter'
  )
);

const ManageImageStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'ManageImageStoreFilter' */ 'Modules/Execute/subModules/ManageImage/ManageImageStoreFilter'
  )
);

const CampaignStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'CampaignStoreFilter' */ 'Modules/Execute/subModules/Campaign/components/CampaignStoreFilter/CampaignStoreFilter'
  )
);

const ReconciliationStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'ReconciliationStoreFilter' */ 'components/Finances/Reconciliation/ReconciliationStoreFilter/ReconciliationStoreFilter'
  )
);

const ConnectJournalDrawer = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'ConnectJournalDrawer' */ 'Modules/Connect/components/ConnectJournalDrawer/ConnectJournalDrawer'
  )
);

const StockValidationStoreFilter = LazyLoadWithRetry(() =>
  import(
    /* webpackChunkName: 'StockValidationStoreFilter' */ 'Modules/Execute/subModules/StockValidation/components/StockValidationStoreFilter'
  )
);

const MainHeader = props => {
  const { isChannelConnected, className } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = window.location.pathname;
  const parsedQueryString = queryString.parse(window.location.search);
  const infoMsgId = useId();
  const selectedTab = parsedQueryString?.tab || '';
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isHistoricalDataModalOpen, setIsHistoricalDataModalOpen] = useState(false);
  const globalFilterPath = !_isEmpty(pathname) && _some(globalFilterRoute, route => pathname.match(route));
  const merchantData = useSelector(state => state?.User?.accountDetails);
  const navigationMenus = useSelector(state => state?.Navigation);
  const userDetails = useSelector(state => state?.User?.accountDetails?.userDetails);
  const workspaceSelectedDashboardConfig = useSelector(state => state?.WorkSpace?.selectedDashboardConfig);
  const allChannels = useSelector(state => state.Account?.stores?.nicknames);
  const allSourceChannels = useSelector(state => state.Account?.stores?.sourceNicknames);
  const channelGroup = useSelector(state => state?.User?.accountDetails?.userDetails?.channelGroup);
  const rcaList = useSelector(state => state?.Rca?.RcaList);
  const RecommendationsSelectedValue = useSelector(state => state?.Recommendations?.selectedStores);
  const countryCode = useSelector(state => state?.User?.userData?.countryCode);
  const isDemoHost = useSelector(state => state?.Account?.isDemoHost);
  const {
    selectedFilter: filters,
    isPopOverOpened,
    dashboard,
    lastSelectedDashboard,
    isFilterValueChanged
  } = useSelector(state => state?.ChannelFilter);
  const userData = useSelector(state => state?.User);
  const [isAdminUser, setIsAdminUser] = useState(userData?.userData?.userName?.includes('[admin]'));
  const connectedList = useSelector(state => state.Account?.stores?.list);
  const isHomePage = pathname === routesMapping['home'];
  const globalDateRange = useSelector(state => state.globalDateRange);
  const connectJournal = useSelector(state => state?.ConnectJournal?.global);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isHopperEnabled, setIsHopperEnabled] = useState(false);
  const storeStepDetails = useSelector(state => state?.ConnectJournal?.global);

  const redirectFromHoppr = window.localStorage.getItem('redirectFromHoppr');

  useEffect(() => {
    if (redirectFromHoppr) {
      setIsHopperEnabled(true);
    }
  }, [redirectFromHoppr]);

  const currentCoutryDetails =
    _find(countryDetails, country => country?.countryCode === merchantData?.countryCode) || {};

  const storeGroupEnabledSubMenuList = [
    'business-dashboard',
    'marketing-dashboard',
    'advertisements',
    'finance-dashboard'
  ];
  const storeGroupEnabledMainMenuList = ['/analyze', '/executor'];

  // --- Route based dispatch object
  const routeBasedDispatchObject = {
    '/executor/orders': OrdersConstants.APPLIED_STORE_FILTER,
    '/executor/inventory': InventoryConstants.APPLIED_STORE_FILTER,
    '/executor/stock-validation': StockValidationConstants.APPLIED_STORE_FILTER,
    '/analyze/finance-reconciliation/stores': ReconciliationConstants.APPLIED_STORE_FILTER
  };

  const filteredNicknameList = getInitialFilteredChannels(allChannels, '', true);
  // --- Route based selected values
  const routeBasedSelectedValue = {
    '/predict': RecommendationsSelectedValue,
    '/predict/list': RecommendationsSelectedValue
  };

  //! For different routes we are taking different channels depends on route. e.g. RCA we are taking marketplaces channels
  const routeBasedSelectedTab = {
    '/predict': 'predict',
    '/predict/list': 'predict'
  };

  const routeBasedStoresFilter = {
    '/executor/orders': (
      <Suspense fallback={<></>}>
        <OrdersStoreFilter />
      </Suspense>
    ),
    '/executor/inventory': (
      <Suspense fallback={<></>}>
        <InventoryStoreFilter />
      </Suspense>
    ),
    '/executor/manage-image': (
      <Suspense fallback={<></>}>
        <ManageImageStoreFilter />
      </Suspense>
    ),
    // '/executor/promotion': (
    //   <Suspense fallback={<></>}>
    //     <CampaignStoreFilter />
    //   </Suspense>
    // ),
    '/executor/product-master': (
      <Suspense fallback={<></>}>
        <ProductMasterStoreFilter />
      </Suspense>
    ),
    '/executor/listing-batch': (
      <Suspense fallback={<></>}>
        <ListingBatchStoreFilter />
      </Suspense>
    ),
    '/executor/offline-orders': (
      <Suspense fallback={<></>}>
        <OfflineOrderStoreFilter />
      </Suspense>
    ),
    '/analyze/finance-reconciliation/stores': (
      <Suspense fallback={<></>}>
        <ReconciliationStoreFilter />
      </Suspense>
    ),
    '/executor/stock-validation': (
      <Suspense fallback={<></>}>
        <StockValidationStoreFilter />
      </Suspense>
    )
  };

  // --- Initial State
  const routedBasedInitialState = {
    '/analyze/rca': rcaList
  };

  const getNavigationMenus = navigationMenus => {
    if (!_isEmpty(navigationMenus)) {
      const connectedChannels =
        _filter(connectedList, item => {
          const channelName = item?.split('-')[0];
          return _includes(extractSupportedChannels, channelName);
        }) || [];
      if (connectedChannels.length < 2) {
        const filteredObj = _mapValues(navigationMenus, value => {
          if (Array.isArray(value)) {
            return _filter(value, item =>
              Array.isArray(item) ? !_includes(item, 'nav_extract') : item?.id !== 'nav_extract'
            );
          }
          return value;
        });
        if (!_isEmpty(filteredObj)) {
          return filteredObj;
        }
      }
    }
    return navigationMenus;
  };

  const data = {
    navigationMenus: getNavigationMenus(navigationMenus),
    isDataLakeReady: merchantData?.isDataLakeReady || false
  };

  const getNickNameIDList = nicknames => {
    let nickNameList = '';
    for (var index in nicknames) {
      let channel = nicknames[index]?.id?.split('-')[0];
      if (nicknames[index]?.status !== 'X' && deepDiveReportEnabledChannel.includes(channel)) {
        if (_isEmpty(nickNameList)) {
          nickNameList += nicknames[index]?.id;
        } else {
          nickNameList += ',' + nicknames[index]?.id;
        }
      }
    }
    return nickNameList;
  };

  useEffect(() => {
    const channels = getAllStores(allChannels, 'custom_dashboard', false);
    const routeBasedInitialSelection = {
      // '/analyze/workspace': channels,
      '/analyze/rca': getAllStores(allChannels, 'rca'),
      '/analyze/marketing-deep-dive': channels,
      '/analyze/attribution-model': getAllStores(allChannels, 'attribution'),
      '/analyze/insights': channels,
      '/connect/mapping': getAllStores(allChannels, 'brandMapping'),
      '/executor/mapping': getAllStores(allChannels, 'brandMapping'),
      '/analyze/finance-orderIncome': getOrderIncomeSelectedStores(
        isAdminUser,
        merchantData,
        connectedList,
        allChannels,
        'selectedChannel'
      ),
      '/predict': channels,
      '/predict/list': channels,
      '/analyze/product-deepdive': channels,
      [routesMapping['home']]: channels
    };

    const channelFilterObject = routeBasedInitialSelection[pathname] || channels;
    if ((_isEmpty(filters) || _isEmpty(filters?.value)) && !_isEmpty(channelFilterObject?.value)) {
      dispatch(setChannelFilter(channelFilterObject));
    }
  }, [dispatch, allChannels, filters, pathname, merchantData, connectedList]);

  useEffect(() => {
    if (isPopOverOpened) {
      setIsPopoverOpen(true);
    }
  }, [isPopOverOpened]);

  useEffect(() => {
    if (isFilterValueChanged) {
      dispatch(setIsFilterValueChanged(false));
    } else if (
      dashboard?.[lastSelectedDashboard]?.value &&
      dashboard?.[workspaceSelectedDashboardConfig?.code]?.value &&
      !isFilterValueChanged
    ) {
      const isChannelFilterChanged = _isEqual(
        dashboard[lastSelectedDashboard]?.value,
        dashboard[workspaceSelectedDashboardConfig?.code]?.value
      );

      if (!isChannelFilterChanged) {
        notification['info']({
          key: infoMsgId,
          placement: 'topRight',
          duration: 4,
          message: (
            <span className='Graas-heading size-5 dont-break'>
              {t('GRAAS_PLEASE_NOTE_SELECTED_CHANNELS_ARE_UPDATED_GRAAS')}
            </span>
          ),
          style: {
            marginLeft: '-320px',
            width: '380px',
            left: '50%',
            marginTop: '30px'
          }
        });
      }
    }
  }, [lastSelectedDashboard, workspaceSelectedDashboardConfig?.code, dashboard]);

  const getDropDownOptions = () => {
    let optionList = [{ value: 'defaultGroup', label: t('GRAAS_DEFAULT_GROUP_GRAAS') }];
    _map(userDetails?.channelGroup, channelGroupObj => {
      if (channelGroupObj?.status !== 'X') {
        optionList?.push({ value: channelGroupObj?.id, label: channelGroupObj?.name });
      }
    });
    return optionList;
  };
  const getChannelGroup = () => {
    if (!_isEmpty(channelGroup)) {
      const DefaultGroupObj = _find(channelGroup, group => group?.id === 'DefaultGroup-1');
      if (_isEmpty(DefaultGroupObj)) {
        let channels = [];
        _map(allChannels, nickNameObj => {
          if (!skippedChannels.includes(nickNameObj?.id?.split('-')[0]) && nickNameObj?.status !== 'X') {
            channels?.push({ nicknameID: nickNameObj?.id, status: nickNameObj?.status });
          }
        });
        channelGroup.unshift({
          id: 'DefaultGroup-1',
          name: 'Default Group',
          channels: channels,
          preferredCurrency: 'account',
          default: false
        });
      }
    }
    const activeChannelGroup = _filter(channelGroup, group => group?.status !== 'X');
    return activeChannelGroup;
  };
  const filterSelectedName = (selectedChannelName, tabName) => {
    let filterVal = '';
    if (tabName === 'channel') {
      let channelObj = allChannels?.find(obj => obj.id === selectedChannelName);
      if (!_isEmpty(channelObj)) {
        let channelName = selectedChannelName.split('-')[0];
        channelName = channelName.charAt(0).toUpperCase() + channelName.slice(1);
        filterVal = `${channelName}-${channelObj.value}`;
      }
    } else {
      let channelGrpObj = channelGroup.find(obj => obj.id === selectedChannelName);
      if (!_isEmpty(channelGrpObj)) {
        filterVal = channelGrpObj?.name;
      }
    }
    return filterVal;
  };

  // Check URL parameters on component mount
  useEffect(() => {
    const parsed = queryString?.parse(window?.location?.search);
    if (parsed?.connectJournal === 'drawer' || parsed?.dataAccuracy === 'drawer') {
      setOpenDrawer(true);
    }
  }, [dispatch]);

  const handledDataDoctorRedirect = () => {
    updateQueryParams({ connectJournal: 'drawer' });
    setOpenDrawer(true);
  };

  //* For channel filter btn text
  const selectedFilterMessage = () => {
    let filtersObject = filters;
    if (location.pathname === '/analyze/workspace') {
      filtersObject = dashboard[parsedQueryString?.tab];
    }
    if (filtersObject?.value?.length === 1) {
      const channelObject = _find(allChannels, ch => ch.id === filtersObject.value[0]) || {};
      if (!_isEmpty(channelObject)) {
        return `${channelObject?.id?.split('-')[0]}-${channelObject?.value}`;
      }
    }
    const totalChannel = getAllStores(allChannels, 'custom_dashboard', true, filtersObject?.isInactiveChannelEnabled)
      ?.value?.length;
    if (totalChannel === filtersObject?.value?.length) {
      return t('GRAAS_ALL_CHANNELS_GRAAS');
    }
    return `${filtersObject?.value?.length || 0} ${t('GRAAS_CHANNELS_GRAAS')}`;
  };

  const handledSelectStore = value => {
    let payload = value;
    if (location.pathname === '/analyze/workspace') {
      let payload = { key: parsedQueryString?.tab, value };
      dispatch(setIsFilterValueChanged(true));
      dispatch(setChannelFilterForCustomDashboard(payload));
    } else if (globalFilterPath) {
      dispatch(setChannelFilter(payload));
    } else {
      if (!_isEmpty(selectedTab)) {
        payload = pathname.includes('/analyze/insights')
          ? routeBasedSelectedValue['/analyze/insights']
          : routeBasedSelectedValue[pathname];
        payload[selectedTab] = value;
      }
      dispatch({ type: routeBasedDispatchObject[pathname], payload });
    }
    setIsPopoverOpen(false);
  };

  const getGlobalSelectedFilterValue = () => {
    if (globalFilterPath) {
      if (location.pathname === '/analyze/workspace') {
        return dashboard?.[parsedQueryString?.tab]?.value;
      }

      return filters?.value ?? [];
    }
    return [];
  };
  const getSelectedStoreValue = () => {
    if (globalFilterPath) {
      return getAllStores(allChannels, 'custom_dashboard', false, true).value;
    }

    if (routeBasedSelectedValue[pathname]?.[selectedTab]?.type === 'stores')
      return routeBasedSelectedValue[pathname]?.[selectedTab]?.value;
    if (routeBasedSelectedValue[pathname]?.type === 'stores') return routeBasedSelectedValue[pathname]?.value;
    if (pathname.includes('/analyze/insights') && routeBasedSelectedValue['/analyze/insights']?.type === 'stores')
      return routeBasedSelectedValue['/analyze/insights']?.value;
    return [];
  };

  const getFilterValue = () => {
    const allChannelGroups = _map(channelGroup, 'name');
    const path = routeBasedSelectedValue[pathname.includes('/analyze/insights') ? '/analyze/insights' : pathname];
    const selectedTabDetails = path?.[selectedTab] ? path?.[selectedTab] : path;
    let selectedChannelGroups = [];
    if (selectedTabDetails?.type === 'store_group') {
      selectedChannelGroups = selectedTabDetails?.storeIdList || [];
    }
    let selectedChannels =
      selectedTabDetails?.type === 'stores'
        ? selectedTabDetails?.value
        : routeBasedSelectedValue[pathname]?.type === 'stores'
        ? routeBasedSelectedValue[pathname]?.value
        : [];
    const inactiveChannels = _map(
      _filter(filteredNicknameList, item => item.status === 'X'),
      'id'
    );
    let selectedChannelsCount = 0;
    let isInactiveChannelEnabled = selectedTabDetails
      ? selectedTabDetails?.isInactiveChannelEnabled
      : routeBasedSelectedValue[pathname]?.isInactiveChannelEnabled;
    let allChannelsCount = isInactiveChannelEnabled
      ? filteredNicknameList?.length
      : _filter(filteredNicknameList, item => item.status !== 'X')?.length;

    if (isInactiveChannelEnabled) {
      selectedChannelsCount = selectedChannels?.length;
    } else {
      selectedChannelsCount = selectedChannels?.filter(channel => !inactiveChannels.includes(channel))?.length;
    }
    let selectedChannelName = selectedChannelsCount === 1 ? selectedChannels?.[0] : '';
    const filterValue =
      selectedChannelsCount === 1
        ? filterSelectedName(selectedChannelName, 'channel')
        : selectedChannelsCount === allChannelsCount
        ? `${t('GRAAS_ALL_CHANNELS_GRAAS')}`
        : selectedChannelsCount !== 0
        ? `${selectedChannelsCount} ${t('GRAAS_CHANNELS_GRAAS')}`
        : selectedChannelsCount === 0 && selectedChannelGroups?.length === 1
        ? filterSelectedName(selectedChannelGroups?.[0], 'channelGroup')
        : selectedChannelsCount === 0 && allChannelGroups?.length === selectedChannelGroups?.length
        ? `${t('GRAAS_ALL_CHANNEL_GROUPS_GRAAS')}`
        : selectedChannelsCount === 0 && selectedChannelGroups?.length !== 1
        ? `${selectedChannelGroups?.length} ${t('GRAAS_CHANNEL_GROUPS_GRAAS')}`
        : `${t('GRAAS_CHANNEL_GROUPS_GRAAS')}`;
    return filterValue;
  };

  const getSelectedStoreGroupValues = () => {
    if (routeBasedSelectedValue[pathname]?.[selectedTab]?.type === 'store_group')
      return routeBasedSelectedValue[pathname]?.[selectedTab]?.storeIdList;
    if (routeBasedSelectedValue[pathname]?.type === 'store_group')
      return routeBasedSelectedValue[pathname]?.storeIdList;
    if (pathname.includes('/analyze/insights') && routeBasedSelectedValue['/analyze/insights']?.type === 'store_group')
      return routeBasedSelectedValue['/analyze/insights']?.storeIdList;
    return [];
  };

  const isShowCurrency = pathname => {
    // --- ignore tab array
    const ignoreTabArray = [];
    const routes = ['/analyze/workspace'];
    if (routes.includes(pathname) && !ignoreTabArray?.includes(selectedTab)) {
      return true;
    }

    return false;
  };

  const isShowGroupByChannel = pathname => {
    // --- ignore tab array
    const ignoreTabArray = [''];
    let routes = ['/analyze/workspace'];
    if (routes.includes(pathname) && !ignoreTabArray?.includes(selectedTab)) {
      return true;
    }

    return false;
  };

  const historicalDataPermissions = () => {
    let pathName = window.location.pathname;
    if (pathName?.includes('analyze') || pathName?.includes('connect')) {
      return <HistrocalDataStatus />;
    }
    return <></>;
  };

  //! OLD StoreGroup dropdown
  const getStoreGroupDropDown = () => {
    // once we enabled storeGroup filter in all page we need to remove below conditions
    let isSubMenuPerMission = false;
    let isMainMenuPerMission = false;
    let dropDownOptionsList = getDropDownOptions();
    let pathname = window.location.pathname;
    _map(storeGroupEnabledSubMenuList, storeGroupEnabledSubMenu => {
      if (pathname.includes(storeGroupEnabledSubMenu)) {
        isSubMenuPerMission = true;
      }
      if (pathname.includes('workspace') && workspaceSelectedDashboardConfig?.code === 'custom_dashboard') {
        isSubMenuPerMission = false;
      }
    });

    _map(storeGroupEnabledMainMenuList, storeGroupEnabledMainMenu => {
      if (pathname.includes(storeGroupEnabledMainMenu)) {
        isMainMenuPerMission = true;
      }
    });
    if (isSubMenuPerMission && isMainMenuPerMission) {
      return (
        <div className='store_filter_dropDown'>
          <Select
            defaultValue='defaultGroup'
            style={{ width: '100%' }}
            dropdownRender={menu => (
              <div>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                  <button
                    onClick={e => (window.location = '/connect/store-group?isAddNewStore=true')}
                    className='c-button is-link'
                  >
                    <PlusOutlined /> {t('GRAAS_ADD_CHANNEL_GROUP_GRAAS')}
                  </button>
                </div>
              </div>
            )}
            options={dropDownOptionsList}
            onChange={e => {
              let list = [];
              const getChannelsList = _find(userDetails?.channelGroup, channel => channel?.id === e) || {};
              if (!_isEmpty(getChannelsList)) list = getChannelsList?.channels || [];
              dispatch({ type: DashboardConfigConstants.SET_SELECTED_GROUP, payload: e });
              dispatch({ type: DashboardConfigConstants.SET_SELECTED_GROUP_CHANNELS, payload: list });
            }}
          />
        </div>
      );
    } else {
      // will remove after enabled Store group filter in all pages.
      dispatch({ type: DashboardConfigConstants.SET_SELECTED_GROUP, payload: 'defaultGroup' });
    }
  };

  // *  redirect to new connect microservice if user hits connect
  // if (pathname === routesMapping['connect'] || pathname === '/connect') {
  //   window.location.href = `${process.env.REACT_APP_API_DOMAIN}${routesMapping['connect']}${
  //     _isEmpty(parsedQueryString) ? '' : `?${queryString.stringify(parsedQueryString)}`
  //   }`;
  // }

  const getDisabledChannels = () => {
    if (location.pathname.includes('/analyze/rca')) {
      return rcaIgnoreChannels;
    }

    if (location.pathname.includes('/analyze/product-deepdive')) {
      return productDeepDiveIgnoreChannels;
    }

    if (location.pathname.includes('/analyze/attribution-model')) {
      return getAllStores(allChannels, 'attribution')?.disabledValue;
    }

    if (location.pathname.includes('/connect/mapping') || location.pathname.includes('/executor/mapping')) {
      return getAllStores(allChannels, 'brandMapping')?.disabledValue;
    }

    if (location.pathname.includes('/analyze/finance-orderIncome')) {
      return getOrderIncomeSelectedStores(isAdminUser, merchantData, connectedList, allChannels, 'disabledChannel');
    }

    if (location.pathname.includes('/analyze/workspace')) {
      return dashboard[parsedQueryString?.tab]?.disabledValue;
    }

    return [];
  };

  const getCustomDisabledMsg = pathname => {
    if (_includes(pathname, '/analyze/workspace')) {
      return { text: 'GRAAS_CUSTOM_DISABLED_CHANNEL_TOOLIP_MSG_GRAAS', 0: 'dashboard' };
    }
    return { text: 'GRAAS_DISABLED_CHANNEL_TOOLTIP_MSG_GRAAS' };
  };

  const getDataDoctrBadgeDetails = type => {
    const { isDataAvailable, data, inProgressDataAvailable } = connectJournal || {};

    if (!isDataAvailable) {
      return type === 'color' ? 'Orange' : 'in progress';
    }

    if (_isEmpty(data)) {
      return type === 'color' ? 'green' : 'complete';
    }

    if (!inProgressDataAvailable) {
      return type === 'color' ? 'red' : 'incomplete';
    }

    return type === 'color' ? 'Orange' : 'in progress';
  };

  return (
    <>
      <header className={`c-header ${className}`}>
        <div className='header_logo'>
          <Link to={routesMapping['home']} className={`header_link ${isHomePage ? 'active' : ''}`}>
            <img className='logo' src={countryCode === 'IN' ? GraasBetaLogo : GraasLogo} alt='graas' />
          </Link>
        </div>
        <div className='header_nav'>
          <HeaderMenuBar data={data} isChannelConnected={isChannelConnected} />
        </div>
        <Space size={12} align='center'>
          {!_isEmpty(allChannels) &&
            // TC-16344: Check if any channel has a status that is 'A' or if any channel does not have a status
            _some(allChannels, channel => !channel?.status || channel?.status === 'A') &&
            !_every(
              allChannels,
              channel =>
                channel?.id?.toLowerCase()?.includes('offline') || channel?.id?.toLowerCase()?.includes('sellinall')
            ) &&
            !connectJournal?.loading && (
              <button className='settingButton autoWidth clean' onClick={() => handledDataDoctorRedirect()}>
                <span className='filterValue topPadding'>
                  <Badge className={'dataDoctrBadge'} dot size={'default'} color={getDataDoctrBadgeDetails('color')}>
                    <Icon style={{ cursor: 'pointer' }} name='cloud_sync' size='medium' />
                  </Badge>
                  &nbsp; {t('GRAAS_DATA_DOCTOR_GRAAS')}
                </span>
              </button>
            )}

          {isHopperEnabled && (
            <GraasHopperV2
              currencyData={{
                countryName: currentCoutryDetails?.countryName || '',
                merchantName: merchantData?.displayName || '',
                currencyCode: merchantData?.currencyCode || '',
                currencySymbol: merchantData?.currencySymbol || '',
                timezone: merchantData?.timeZone || '',
                userName: userData?.userData?.userName,
                merchantID: userData?.userData?.merchantID,
                email: userDetails?.SELLinALLAuth?.SELLinALLId || ''
              }}
              channelFilterProps={{
                initialChannels:
                  _filter(getInitialFilteredChannels(allChannels, '', true), channel => channel?.status === 'A') || [],
                allChannels:
                  _filter(
                    allChannels,
                    channel =>
                      !channel?.id.includes('googleAnalytics') &&
                      !channel?.id.includes('sellInAll') &&
                      channel?.status === 'A'
                  ) || [],
                selectedChannels: _compact(
                  _map(getInitialFilteredChannels(allChannels, '', true), channel => {
                    if (!channel?.id.includes('sellInAll') && channel?.status === 'A') {
                      return channel?.id;
                    }
                  }) || []
                )
              }}
              actionRequiredChannel={connectJournal?.lastUpdatedChannelList}
              isHeaderMenu={false}
              redirectFromHoppr={redirectFromHoppr}
            />
          )}
          {isHistoricalDataModalOpen && (
            <Modal
              title={null}
              footer={null}
              open={isHistoricalDataModalOpen}
              width={connectJournal?.isDataAvailable ? 450 : 300}
              closable={false}
              destroyOnClose
              className='chromeExtensionModalTooltip'
              style={{
                top: 20,
                left: connectJournal?.isDataAvailable ? 0 : 200
              }}
              onCancel={() => setIsHistoricalDataModalOpen(false)}
            >
              <Suspense fallback={<></>}>
                <GraasChromeExtensionProcess
                  isChromeExtensionInstalled={merchantData?.userDetails?.isChromeExtensionInstalled || false}
                  onCloseExtensionModal={() => {
                    setIsHistoricalDataModalOpen(false);
                    onCloseModal();
                  }}
                  actionRequiredChannel={connectJournal?.lastUpdatedChannelList}
                />
              </Suspense>
            </Modal>
          )}

          {historicalDataPermissions()}
          {isHomePage && (
            <GraasDateRangePicker
              isDisableFutureDate
              selectedDateRange={globalDateRange?.dateRange}
              onUpdateDate={value => {
                dispatch(
                  updateDateRangeState({
                    ...globalDateRange,
                    dateRange: value,
                    comparedDateRange: computeCompareDateRange(value)
                  })
                );
              }}
            />
          )}

          {openDrawer && (
            <Suspense fallback={<></>}>
              <ConnectJournalDrawer
                openDrawer={openDrawer}
                setOpenDrawer={setOpenDrawer}
                actionRequiredChannel={connectJournal?.lastUpdatedChannelList}
                populateValues={{
                  status: getDataDoctrBadgeDetails('status')
                }}
              />
            </Suspense>
          )}
          {getStoreGroupDropDown()}
          {
            // !_isEmpty(getInitialFilteredChannels(allChannels, routeBasedSelectedTab[pathname] || selectedTab)) &&
            //   isStoreGroupPickerComponentEnabled(pathname) &&
            !_isEmpty(routeBasedStoresFilter[pathname]) ? (
              routeBasedStoresFilter[pathname]
            ) : pathname.startsWith('/executor/promotion-beta/details') ? (
              <Suspense fallback={<></>}>
                <CampaignStoreFilter store={parsedQueryString?.channel} />
              </Suspense>
            ) : (
              <>
                {globalFilterPath && (
                  <Popover
                    open={isPopoverOpen}
                    placement='bottomLeft'
                    title={null}
                    onOpenChange={e => {
                      if (!routedBasedInitialState[pathname]?.loading) {
                        setIsPopoverOpen(e);
                      }
                    }}
                    destroyTooltipOnHide
                    overlayClassName='c-customPopover position-fixed'
                    content={
                      <Suspense fallback={<></>}>
                        <GraasStoreGroupPicker
                          initialChannels={getInitialFilteredChannels(allChannels, '', true)}
                          initialSourceChannels={getInitialFilteredChannels(allSourceChannels, '', true)}
                          selectedChannels={globalFilterPath ? getGlobalSelectedFilterValue() : getSelectedStoreValue()}
                          allChannels={allChannels}
                          disabledChannels={getDisabledChannels()}
                          selectedChannelGroup={getSelectedStoreGroupValues()}
                          channelGroup={getChannelGroup()}
                          onCancel={() => setIsPopoverOpen(false)}
                          onApplyChanges={e => handledSelectStore(e)}
                          isChannelEnabledFlag={
                            routeBasedSelectedValue[pathname]?.[selectedTab]?.isChannelEnabled ||
                            routeBasedSelectedValue[pathname]?.isChannelEnabled
                          }
                          isInactiveChannelEnabled={
                            globalFilterPath
                              ? dashboard?.[parsedQueryString?.tab]?.isInactiveChannelEnabled ??
                                filters?.isInactiveChannelEnabled
                              : routeBasedSelectedValue[pathname]?.[selectedTab]?.isInactiveChannelEnabled ||
                                routeBasedSelectedValue[pathname]?.isInactiveChannelEnabled
                          }
                          isChannelCurrencyEnabled={
                            globalFilterPath
                              ? dashboard?.[parsedQueryString?.tab]?.isChannelCurrencyEnabled ??
                                filters?.isChannelCurrencyEnabled
                              : routeBasedSelectedValue[pathname]?.[selectedTab]?.isChannelCurrencyEnabled ||
                                routeBasedSelectedValue[pathname]?.isChannelCurrencyEnabled
                          }
                          isShowSourceCurrency={isShowCurrency(pathname)}
                          isShowGroupByChannel={isShowGroupByChannel(pathname)}
                          customDisabledMsg={getCustomDisabledMsg(location.pathname)}
                        />
                      </Suspense>
                    }
                    trigger='click'
                    qa-data='mainHeader-popover-storePicker'
                  >
                    <button
                      className='c-button is-bordered sm'
                      qa-data='GraasStoreGroupPicker-btn'
                      disabled={routedBasedInitialState[pathname]?.loading || false}
                    >
                      <span className='filterValue capitalize'>
                        <Icon name='filter_list' size='default' />
                        &nbsp; {globalFilterPath ? selectedFilterMessage() : getFilterValue()}
                        <Icon name='expand_more' size='default' />
                      </span>
                    </button>
                  </Popover>
                )}
              </>
            )
          }
        </Space>

        <div className='header_settings'>
          <ul>
            <li>
              <Suspense fallback={<></>}>
                {isDemoHost ? (
                  <DemoUserProfile
                    merchantName={merchantData?.displayName || merchantData?.merchantName || merchantData?.merchantID}
                    isChannelConnected={isChannelConnected}
                  />
                ) : (
                  <UserProfile
                    merchantName={merchantData?.displayName || merchantData?.merchantName || merchantData?.merchantID}
                    isChannelConnected={isChannelConnected}
                  />
                )}
              </Suspense>
            </li>
          </ul>
        </div>
      </header>
    </>
  );
};

export default MainHeader;
